import { PaginationInformation } from './powderguide-api'

interface PaginationPage {
    key: string,
    type: 'page',
    page: number,
    isCurrent: boolean,
}

interface PaginationEllipsis {
    key: string,
    type: 'ellipsis',
}

type PaginationElement = PaginationPage | PaginationEllipsis;

type PaginationControllerPageChangeCallback = () => void;

type PaginationConfiguration = {
    startFixedElements: number,
    endFixedElements: number,
    elementsAroundCurrent: number,
};

export class PaginationController implements PaginationInformation {
    public information: PaginationInformation;
    public elements: ReadonlyArray<PaginationElement> = [];

    constructor(
        pagination: PaginationInformation,
        private configuration: PaginationConfiguration = { elementsAroundCurrent: 2, startFixedElements: 2, endFixedElements: 2 },
        private pageChangeCallback: PaginationControllerPageChangeCallback | null = null
    ) {
        this.update(pagination);
    }

    public goToPage(page: number) {
        this.update({ ...this.information, page });
        this.pageChangeCallback();
    }

    public update(pagination: PaginationInformation) {
        this.information = { ...pagination };

        const elements: PaginationElement[] = [ ];
        const minNumPages = this.configuration.startFixedElements + this.configuration.endFixedElements + this.configuration.elementsAroundCurrent * 2 + 1;
        if (pagination.last_page <= minNumPages) {
            for (let i = 1; i <= pagination.last_page; i++) {
                elements.push({ type: 'page', page: i, isCurrent: i === pagination.page, key: `page_${i}` });
            }
        } else {

            const startOfLeftPortion = 1;
            const endOfLeftPortion = startOfLeftPortion + this.configuration.startFixedElements - 1;
            const startOfCenterPortion = Math.max(pagination.page - this.configuration.elementsAroundCurrent, endOfLeftPortion + 1)
            const endOfCenterPortion = Math.min(startOfCenterPortion + this.configuration.elementsAroundCurrent * 2 + 1, pagination.last_page - this.configuration.endFixedElements);
            const startOfRightPortion = Math.max(pagination.last_page - this.configuration.endFixedElements + 1, endOfCenterPortion + 1);
            const endOfRightPortion = pagination.last_page;

            elements.push(...this.buildPages(startOfLeftPortion, endOfLeftPortion));
            if (endOfLeftPortion + 1 < startOfCenterPortion) {
                elements.push({ type: 'ellipsis', key: 'left_ellipsis' });
            }
            elements.push(...this.buildPages(startOfCenterPortion, endOfCenterPortion));
            if (endOfCenterPortion + 1 < startOfRightPortion) {
                elements.push({ type: 'ellipsis', key: 'right_ellipsis' });
            }
            elements.push(...this.buildPages(startOfRightPortion, endOfRightPortion));
        }
        this.elements = elements;
    }

    private buildPages(start: number, end: number): PaginationElement[] {
        const pages: PaginationElement[] = [ ];
        for (let i = start; i <= end; i++) {
            pages.push({ type: 'page', page: i, isCurrent: i === this.information.page, key: `page_${i}` });
        }
        return pages;
    }

    public get from_item(): number {
        return this.information.from_item;
    }
    public get last_page(): number | null {
        return this.information.last_page;
    }
    public get next_page(): number {
        return this.information.next_page;
    }
    public get page(): number {
        return this.information.page;
    }
    public get per_page(): number {
        return this.information.per_page;
    }
    public get previous_page(): number | null {
        return this.information.previous_page;
    }
    public get to_item(): number {
        return this.information.to_item;
    }
    public get total_items(): number {
        return this.information.total_items;
    }
}
